import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Subject } from '@/interface/types'
import { api } from '@/lib/axios'

/**
 * Custom hook for managing subject data
 * @returns Object containing subject data and mutation functions
 */
export default function useSubject() {
  const queryClient = useQueryClient()

  const {
    data: subjects = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['subjects'],
    queryFn: async () => {
      // HTTP-only cookies sent automatically via api instance (withCredentials: true)
      const response = await api.get<Subject[]>('/subject')
      return response.data
    },
  })

  const createSubject = useMutation({
    mutationFn: async (newSubject: Partial<Subject>) => {
      // HTTP-only cookies sent automatically via api instance (withCredentials: true)
      const response = await api.post<Subject>('/subject', newSubject)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subjects'] })
    },
  })

  const updateSubject = useMutation({
    mutationFn: async ({ id, ...data }: Partial<Subject> & { id: string }) => {
      // HTTP-only cookies sent automatically via api instance (withCredentials: true)
      const response = await api.patch<Subject>(`/subject/${id}`, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subjects'] })
    },
  })

  const deleteSubject = useMutation({
    mutationFn: async (id: string) => {
      // HTTP-only cookies sent automatically via api instance (withCredentials: true)
      await api.delete(`/subject/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subjects'] })
    },
  })

  const updateFormulas = useMutation({
    mutationFn: async (updatedSubjects: Subject[]) => {
      // HTTP-only cookies sent automatically via api instance (withCredentials: true)
      const response = await api.patch<Subject[]>(
        '/subject/formulas',
        updatedSubjects
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subjects'] })
    },
  })

  return {
    subjects,
    isLoading,
    isError,
    error,
    createSubject: createSubject.mutate,
    updateSubject: updateSubject.mutate,
    deleteSubject: deleteSubject.mutate,
    updateFormulas: updateFormulas.mutate,
    isCreating: createSubject.isPending,
    isUpdating: updateSubject.isPending,
    isDeleting: deleteSubject.isPending,
  }
}
